"use client";

import { useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@clerk/clerk-react";
import { useQuery, useConvexAuth } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useMutationWithAuthRedirect } from "@/lib/convexAuth";

const ALLOWED_PATH_PREFIXES = ["/login", "/signup", "/onboarding", "/onboarding/voice-guide"];

function isAllowedPath(pathname: string) {
  return ALLOWED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function OnboardingGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isLoaded, isSignedIn } = useAuth();
  const { isAuthenticated: isConvexAuthenticated, isLoading: isConvexLoading } = useConvexAuth();

  const user = useQuery(api.users.getCurrentUser);
  const ensureCurrentUser = useMutationWithAuthRedirect(api.users.ensureCurrentUser);

  // If Clerk says we're signed in but Convex doesn't have a user row yet,
  // create it so onboarding can proceed.
  // Wait for both Clerk and Convex auth to be ready to avoid race conditions.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (isConvexLoading || !isConvexAuthenticated) return;
    if (user !== null) return;
    void ensureCurrentUser({});
  }, [ensureCurrentUser, isLoaded, isSignedIn, isConvexLoading, isConvexAuthenticated, user]);

  const needsVoiceGuide =
    isLoaded && isSignedIn && user !== undefined && user !== null && !user.voice_guide_completed;

  const needsUsername =
    isLoaded && isSignedIn && user !== undefined && user !== null && !user.username;

  useEffect(() => {
    if (isAllowedPath(pathname)) return;

    if (needsVoiceGuide) {
      router.replace("/onboarding/voice-guide");
      return;
    }

    if (needsUsername) {
      const search = searchParams.toString();
      const next = `${pathname}${search ? `?${search}` : ""}`;
      router.replace(`/onboarding?next=${encodeURIComponent(next)}`);
    }
  }, [needsUsername, needsVoiceGuide, pathname, router, searchParams]);

  // Don't flash protected content while we check onboarding state.
  if (!isLoaded || isConvexLoading) {
    return children;
  }

  if (isSignedIn && isConvexAuthenticated && (user === undefined || user === null)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (needsVoiceGuide && !isAllowedPath(pathname)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (needsUsername && !isAllowedPath(pathname)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return children;
}
